/**
 * Parent-student linking and progress sharing via Firestore.
 *
 * Collections:
 *   parentChildLinks/{linkId}  — maps a parent email to a child email
 *   studentProgress/{sanitizedEmail} — aggregated progress snapshot per student
 */
import {
  collection,
  doc,
  getDoc,
  getDocs,
  query,
  setDoc,
  Timestamp,
  where,
} from 'firebase/firestore';
import { getFirestore } from 'firebase/firestore';
import { firebaseApp } from './firebaseConfig';

const db = getFirestore(firebaseApp);

export interface ParentChildLink {
  parentEmail: string;
  childEmail: string;
  childName: string;
  parentName?: string;
}

/** A parent connected to a given child (for the child's "connected parents" list). */
export interface ConnectedParent {
  parentEmail: string;
  parentName: string;
}

/**
 * An account connected to the viewer, with the other party's role *relative to
 * the viewer*: if the viewer is the child, the other party is a "parent"; if the
 * viewer is the parent, the other party is a "child".
 */
export interface Connection {
  email: string;
  name: string;
  role: 'parent' | 'child';
}

export interface StudentProgressSnapshot {
  userId?: string | null;
  email: string;
  name: string;
  totalVerses: number;
  averageScore: number;
  hoursLearned: number;
  dayStreak: number;
  dailySeries: { date: string; avg: number; count: number }[];
  parasha?: { english: string; hebrew: string } | null;
  lastUpdated: Timestamp;
}

export interface ProgressParams {
  email: string;
  name: string;
  userId?: string;
  totalVerses: number;
  averageScore: number;
  hoursLearned: number;
  dayStreak: number;
  dailySeries: { date: string; avg: number; count: number }[];
  parasha?: { english: string; hebrew: string } | null;
}

function sanitizeEmail(email: string): string {
  return email.replace(/[.@]/g, '_');
}

/**
 * Idempotent: creates the mock parent-child link only if it doesn't exist yet.
 * shaul.almog@gmail.com → david.cohen@gmail.com
 */
export async function setupMockParentChildLink(): Promise<void> {
  const linkId = 'shaul_almog_gmail_com__david_cohen_gmail_com';
  const ref = doc(db, 'parentChildLinks', linkId);
  const snap = await getDoc(ref);
  if (!snap.exists()) {
    await setDoc(ref, {
      childEmail: 'shaul.almog@gmail.com',
      parentEmail: 'david.cohen@example.com',
      childName: 'שאול אלמוג',
      createdAt: Timestamp.now(),
    });
  }
}

/** Returns all children linked to the given parent email. */
export async function getChildrenForParent(parentEmail: string): Promise<ParentChildLink[]> {
  if (!parentEmail) return [];
  const q = query(collection(db, 'parentChildLinks'), where('parentEmail', '==', parentEmail));
  const snap = await getDocs(q);
  const result: ParentChildLink[] = [];
  snap.forEach((d) => result.push(d.data() as ParentChildLink));
  return result;
}

/** Returns all parents connected to the given child email (newest API may add ordering). */
export async function getParentsForChild(childEmail: string): Promise<ConnectedParent[]> {
  if (!childEmail) return [];
  const q = query(
    collection(db, 'parentChildLinks'),
    where('childEmail', '==', childEmail.trim().toLowerCase()),
  );
  const snap = await getDocs(q);
  const result: ConnectedParent[] = [];
  snap.forEach((d) => {
    const data = d.data() as ParentChildLink;
    result.push({
      parentEmail: data.parentEmail,
      parentName: data.parentName || (data.parentEmail ? data.parentEmail.split('@')[0] : 'הורה'),
    });
  });
  return result;
}

/**
 * Returns every parent linked to the given child email.
 * Queries only `childEmail == email` so bidirectional legacy docs don't
 * pollute the result with the wrong role.
 */
export async function getParentsForUser(email: string): Promise<Connection[]> {
  if (!email) return [];
  const e = email.trim().toLowerCase();
  const snap = await getDocs(
    query(collection(db, 'parentChildLinks'), where('childEmail', '==', e)),
  );
  const byEmail = new Map<string, Connection>();
  snap.forEach((d) => {
    const data = d.data() as ParentChildLink;
    const key = (data.parentEmail || '').trim().toLowerCase();
    if (!key || byEmail.has(key)) return;
    byEmail.set(key, {
      email: data.parentEmail,
      name: data.parentName?.trim() || key.split('@')[0],
      role: 'parent',
    });
  });
  return Array.from(byEmail.values());
}

/**
 * Returns every child linked to the given parent email.
 * Queries only `parentEmail == email` so bidirectional legacy docs don't
 * pollute the result with the wrong role.
 */
export async function getChildrenForUser(email: string): Promise<Connection[]> {
  if (!email) return [];
  const e = email.trim().toLowerCase();
  const snap = await getDocs(
    query(collection(db, 'parentChildLinks'), where('parentEmail', '==', e)),
  );
  const byEmail = new Map<string, Connection>();
  snap.forEach((d) => {
    const data = d.data() as ParentChildLink;
    const key = (data.childEmail || '').trim().toLowerCase();
    if (!key || byEmail.has(key)) return;
    byEmail.set(key, {
      email: data.childEmail,
      name: data.childName?.trim() || key.split('@')[0],
      role: 'child',
    });
  });
  return Array.from(byEmail.values());
}

/**
 * Returns connections in both directions. Used when a page needs to show
 * both roles (e.g. /connect-child which any user can open).
 * Deduplicates by email; when a pair exists in both directions the explicit
 * display name wins but the role is preserved per direction.
 */
export async function getConnectionsForUser(email: string): Promise<Connection[]> {
  if (!email) return [];
  const [parents, children] = await Promise.all([
    getParentsForUser(email),
    getChildrenForUser(email),
  ]);
  // Merge; if same email appears in both directions keep both role entries
  // only if they are truly different people (shouldn't happen in practice).
  const byEmail = new Map<string, Connection>();
  for (const c of [...parents, ...children]) {
    const key = c.email.trim().toLowerCase();
    if (!byEmail.has(key)) byEmail.set(key, c);
  }
  return Array.from(byEmail.values());
}

/** Saves (or updates) a student's aggregated progress snapshot to Firestore. */
export async function saveStudentProgressSnapshot(params: ProgressParams): Promise<void> {
  const { email } = params;
  if (!email) return;
  const ref = doc(db, 'studentProgress', sanitizeEmail(email));
  await setDoc(
    ref,
    {
      userId: params.userId ?? null,
      email,
      name: params.name,
      totalVerses: params.totalVerses,
      averageScore: params.averageScore,
      hoursLearned: params.hoursLearned,
      dayStreak: params.dayStreak,
      dailySeries: params.dailySeries,
      parasha: params.parasha ?? null,
      lastUpdated: Timestamp.now(),
    },
    { merge: true },
  );
}

/** Fetches a student's progress snapshot by their email. Returns null if not found. */
export async function getStudentProgressSnapshot(
  childEmail: string,
): Promise<StudentProgressSnapshot | null> {
  if (!childEmail) return null;
  const ref = doc(db, 'studentProgress', sanitizeEmail(childEmail));
  const snap = await getDoc(ref);
  if (!snap.exists()) return null;
  return snap.data() as StudentProgressSnapshot;
}
